import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DateInputComponent } from '../../shared/date-input/date-input.component';
import { CapiHealthComponent } from './capi-health/capi-health.component';
import { AcquisitionFunnelComponent } from './funnel/acquisition-funnel.component';
import { AdLeaderboardComponent } from './ad-leaderboard/ad-leaderboard.component';
import { CtaComparisonComponent } from './cta-comparison/cta-comparison.component';
import { RetentionComponent } from './retention/retention.component';
import { LtvComponent } from './ltv/ltv.component';
import { ACQUISITION_MAX_RANGE_DAYS, formatLocalDate, validateAcquisitionRange } from './acquisition-range';

export interface AcquisitionDateRange {
  /** IST day, yyyy-MM-dd. */
  from: string;
  /** IST day, yyyy-MM-dd, inclusive. */
  to: string;
}

interface AcquisitionTab {
  id: 'funnel' | 'ad-leaderboard' | 'cta-comparison' | 'retention' | 'ltv' | 'capi-health';
  label: string;
  icon: string;
}

/**
 * Control Center → Marketing → Acquisition. Hosts the acquisition views behind one shared date
 * picker. Nothing loads until Apply: Apply loads only the active tab, and a loaded tab stays alive
 * (hidden) when the user switches away, so switching back doesn't query again.
 */
@Component({
  selector: 'app-marketing-acquisition',
  standalone: true,
  imports: [CommonModule, FormsModule, DateInputComponent, CapiHealthComponent, AcquisitionFunnelComponent, AdLeaderboardComponent,
    CtaComparisonComponent, RetentionComponent, LtvComponent],
  templateUrl: './acquisition.component.html',
  styleUrl: './acquisition.component.css'
})
export class MarketingAcquisitionComponent implements OnInit {
  readonly maxRangeDays = ACQUISITION_MAX_RANGE_DAYS;

  readonly tabs: AcquisitionTab[] = [
    { id: 'funnel', label: 'Funnel by channel', icon: 'fas fa-filter' },
    { id: 'ad-leaderboard', label: 'Ad leaderboard', icon: 'fas fa-trophy' },
    { id: 'cta-comparison', label: 'WhatsApp vs Web CTA', icon: 'fas fa-balance-scale' },
    { id: 'retention', label: 'Retention', icon: 'fas fa-redo' },
    { id: 'ltv', label: '12-month LTV', icon: 'fas fa-coins' },
    { id: 'capi-health', label: 'Delivery health', icon: 'fas fa-heartbeat' }
  ];
  activeTab: AcquisitionTab['id'] = 'funnel';

  fromDate = '';
  toDate = '';
  rangeError = '';

  /**
   * The range each tab was last applied with; a tab with no entry hasn't loaded yet. Replaced (not
   * mutated) on Apply so the child input sees a change.
   */
  appliedRanges: Partial<Record<AcquisitionTab['id'], AcquisitionDateRange>> = {};

  ngOnInit(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 29);
    this.toDate = formatLocalDate(today);
    this.fromDate = formatLocalDate(thirtyDaysAgo);
  }

  applyRange(): void {
    this.rangeError = validateAcquisitionRange(this.fromDate, this.toDate);
    if (this.rangeError) {
      return;
    }
    this.appliedRanges = { ...this.appliedRanges, [this.activeTab]: { from: this.fromDate, to: this.toDate } };
  }

  /** The active tab's loaded range when it no longer matches the picked dates, else null. */
  get staleRange(): AcquisitionDateRange | null {
    const applied = this.appliedRanges[this.activeTab];
    return applied && (applied.from !== this.fromDate || applied.to !== this.toDate) ? applied : null;
  }

  selectTab(tab: AcquisitionTab['id']): void {
    this.activeTab = tab;
  }
}
